import React, { useCallback, useEffect, useMemo, useState } from "react";
import { cleanCoCoName } from "../helpers/cocoNameHelper";
import {
  FaChevronLeft,
  FaChevronRight,
  FaClipboardCheck,
  FaCog,
  FaFileSignature,
  FaFilter,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaUsers,
} from "react-icons/fa";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "./Breadcrumb";
import { useAppContext } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { useTranslation } from "../context/TranslationContext";
import { formatDateToYYYYMMDD, formatClientCount } from "../helpers/utilityHelper";
import {
  MASTER_SCHEDULE_FILTERS,
  MASTER_SCHEDULE_FILTER_VALUES,
  translateMatterStatus,
} from "../helpers/clientDisplayHelper";
import { fetchCoordinatorCoCoDetails } from "../helpers/apiHelper";
import { getSalesforceBatchWindow } from "../helpers/salesforcePaginationHelper";
import {
  CLAIM_TABS,
  getClaimProgram,
  getClaimShortLabel,
  getEnglishClaimClients,
  normalizeClaimTab,
} from "../helpers/caseClaimHelper";
import "../styles/Views.css";
import CollapsibleSummary from "./CollapsibleSummary";

export const CASE_TABS = {
  ALL: CLAIM_TABS.ALL,
  BHP: CLAIM_TABS.ENGLISH,
  ARD: CLAIM_TABS.DUTCH,
};

const CASE_TAB_ORDER = [
  CASE_TABS.BHP,
  CASE_TABS.ARD,
];

// The public coordinator API validates page sizes up to 100; the backend expands this to a Salesforce batch.
const COORDINATOR_REQUEST_PAGE_SIZE = 100;

// Both claims are always offered, even when a CoCo has no clients in one of
// them: the tab then shows 0 and an empty list.
export const getVisibleCaseTabs = () => [CASE_TABS.ALL, ...CASE_TAB_ORDER];

export const buildCoordinatorRequestParameters = ({
  searchTerm,
  caseTab,
  retainerFilter,
  questionnaireFilter,
  masterScheduleFilter = MASTER_SCHEDULE_FILTERS.ALL,
  sortConfig,
  masterScheduleFiltersEnabled = false,
}) => ({
  pageSize: COORDINATOR_REQUEST_PAGE_SIZE,
  searchTerm: searchTerm.trim(),
  caseTab,
  retainerFilter,
  questionnaireFilter,
  ...(masterScheduleFiltersEnabled ? { masterScheduleFilter } : {}),
  sortKey: sortConfig.key,
  sortDirection: sortConfig.direction,
});

const getCaseProgram = getClaimProgram;
const getCaseShortLabel = getClaimShortLabel;

const percentage = (part, total) => {
  if (!total) return 0;
  return Number(((part / total) * 100).toFixed(2));
};

const summarizeClients = (summary = {}) => {
  const totalClients = summary.totalClients || 0;
  const retainersSigned = summary.retainersSigned || 0;
  const questionnairesCompleted = summary.questionnairesCompleted || 0;
  return {
    ...summary,
    retainersPending: totalClients - retainersSigned,
    retainersSignedPercentage: percentage(retainersSigned, totalClients),
    questionnairesPending: totalClients - questionnairesCompleted,
    questionnairesCompletedPercentage: percentage(questionnairesCompleted, totalClients),
    discontinuanceTotal: summary.discontinuanceTotal || 0,
    discontinuanceNotStarted: summary.discontinuanceNotStarted || 0,
    discontinuanceInProgress: summary.discontinuanceInProgress || 0,
    discontinuanceSubmitted: summary.discontinuanceSubmitted || 0,
    discontinuanceSubmittedPercentage: percentage(
      summary.discontinuanceSubmitted || 0,
      summary.discontinuanceTotal || 0,
    ),
  };
};

const CoordinatorCoCoDetails = () => {
  const { cocoId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser, tokens } = useAuth();
  const { features } = useFeatureFlags();
  const { clientListCache, updateClientListCache } = useAppContext();
  const { t } = useTranslation();
  const portalUserId = user?.portalUserId || user?.userId;
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedCaseTab, setSelectedCaseTab] = useState(CASE_TABS.ALL);
  const [retainerFilter, setRetainerFilter] = useState("ALL");
  const [questionnaireFilter, setQuestionnaireFilter] = useState("ALL");
  const [masterScheduleFilter, setMasterScheduleFilter] = useState(
    MASTER_SCHEDULE_FILTERS.ALL,
  );
  const [sortConfig, setSortConfig] = useState({ key: "partyName", direction: "asc" });
  const [pageHistoriesByQuery, setPageHistoriesByQuery] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hydratedCacheKey, setHydratedCacheKey] = useState(null);
  const [reloadVersion, setReloadVersion] = useState(0);

  const coordinatorAccessDenied = user?.capabilities?.canAccessCoordinator === false;

  const activeCaseTab = selectedCaseTab;
  const requestParameters = useMemo(() => buildCoordinatorRequestParameters({
    searchTerm: debouncedSearchTerm.trim(),
    caseTab: activeCaseTab,
    retainerFilter,
    questionnaireFilter,
    masterScheduleFilter,
    sortConfig,
    masterScheduleFiltersEnabled: features.masterScheduleFilters,
  }), [
    debouncedSearchTerm,
    activeCaseTab,
    features.masterScheduleFilters,
    masterScheduleFilter,
    retainerFilter,
    questionnaireFilter,
    sortConfig,
  ]);
  const queryKey = JSON.stringify(requestParameters);
  const pageHistory = useMemo(
    () => pageHistoriesByQuery[queryKey] || [],
    [pageHistoriesByQuery, queryKey],
  );
  const { batchIndex, offset: batchOffset } = useMemo(
    () => getSalesforceBatchWindow(pageIndex + 1, pageSize),
    [pageIndex, pageSize],
  );
  const details = pageHistory[batchIndex] || null;
  const clients = useMemo(
    () => (Array.isArray(details?.clients) ? details.clients : []),
    [details?.clients],
  );
  const paginatedClients = useMemo(
    () => clients.slice(batchOffset, batchOffset + pageSize),
    [batchOffset, clients, pageSize],
  );
  const summary = useMemo(() => summarizeClients(details?.summary), [details?.summary]);
  const caseTabCounts = useMemo(() => ({
    ALL: summary.totalClients || 0,
    BHP: getEnglishClaimClients(summary),
    ARD: summary.ardClients || 0,
  }), [summary]);
  const visibleCaseTabs = useMemo(() => getVisibleCaseTabs(), []);
  const coordinatorCacheKey = portalUserId && cocoId
    ? `${portalUserId}:${cocoId}`
    : null;

  const requestPage = useCallback(async (continuationToken = null) => {
    const clientData = await fetchCoordinatorCoCoDetails(cocoId, tokens.idToken, {
      ...requestParameters,
      continuationToken,
    });
    setUser((currentUser) => ({
      ...(currentUser || {}),
      capabilities: {
        ...(currentUser?.capabilities || {}),
        canAccessCoordinator: true,
      },
    }));
    return {
      cocoId: clientData?.cocoId || cocoId,
      cocoName: clientData?.cocoName || location.state?.cocoName || null,
      summary: clientData?.summary || {},
      totalSize: clientData?.totalSize || 0,
      continuationToken: clientData?.continuationToken || null,
      clients: Array.isArray(clientData?.clients) ? clientData.clients : [],
    };
  }, [cocoId, location.state?.cocoName, requestParameters, setUser, tokens?.idToken]);

  useEffect(() => {
    if (!coordinatorCacheKey || hydratedCacheKey === coordinatorCacheKey) return;

    const cached = clientListCache[portalUserId]?.coordinatorDetailsByCoCo?.[cocoId];
    if (cached?.viewState) {
      setSearchTerm(cached.viewState.searchTerm || "");
      setDebouncedSearchTerm(cached.viewState.searchTerm || "");
      setSelectedCaseTab(normalizeClaimTab(cached.viewState.selectedCaseTab) || CASE_TABS.ALL);
      setRetainerFilter(cached.viewState.retainerFilter || "ALL");
      setQuestionnaireFilter(cached.viewState.questionnaireFilter || "ALL");
      if (features.masterScheduleFilters) {
        setMasterScheduleFilter(
          cached.viewState.masterScheduleFilter || MASTER_SCHEDULE_FILTERS.ALL,
        );
      }
      setSortConfig(cached.viewState.sortConfig || { key: "partyName", direction: "asc" });
      setPageSize(cached.viewState.pageSize || 10);
      setPageIndex(cached.viewState.pageIndex || 0);
    }
    setPageHistoriesByQuery(cached?.pageHistoriesByQuery || {});
    setHydratedCacheKey(coordinatorCacheKey);
  }, [
    clientListCache,
    cocoId,
    coordinatorCacheKey,
    features.masterScheduleFilters,
    hydratedCacheKey,
    portalUserId,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!coordinatorCacheKey || hydratedCacheKey !== coordinatorCacheKey) return;

    updateClientListCache(portalUserId, (cached) => ({
      ...cached,
      coordinatorDetailsByCoCo: {
        ...(cached.coordinatorDetailsByCoCo || {}),
        [cocoId]: {
          viewState: {
            searchTerm,
            selectedCaseTab,
            retainerFilter,
            questionnaireFilter,
            masterScheduleFilter: features.masterScheduleFilters
              ? masterScheduleFilter
              : undefined,
            sortConfig,
            pageSize,
            pageIndex,
          },
          pageHistoriesByQuery,
        },
      },
    }));
  }, [
    cocoId,
    coordinatorCacheKey,
    hydratedCacheKey,
    pageHistoriesByQuery,
    pageIndex,
    pageSize,
    portalUserId,
    features.masterScheduleFilters,
    masterScheduleFilter,
    questionnaireFilter,
    retainerFilter,
    searchTerm,
    selectedCaseTab,
    sortConfig,
    updateClientListCache,
  ]);

  useEffect(() => {
    if (
      coordinatorAccessDenied
      || !tokens?.idToken
      || !cocoId
      || !coordinatorCacheKey
      || hydratedCacheKey !== coordinatorCacheKey
    ) return;
    if (details) {
      setLoading(false);
      return;
    }

    const previousBatch = pageHistory[batchIndex - 1];
    if (batchIndex > 0 && !previousBatch) {
      setPageIndex(0);
      return;
    }

    let cancelled = false;
    const loadPage = async () => {
      setLoading(true);
      setError("");
      try {
        const page = await requestPage(previousBatch?.continuationToken || null);
        if (cancelled) return;
        setPageHistoriesByQuery((histories) => {
          const history = [...(histories[queryKey] || [])];
          history[batchIndex] = page;
          return { ...histories, [queryKey]: history };
        });
      } catch (requestError) {
        if (!cancelled) {
          console.error("Failed to fetch coordinator CoCo details:", requestError);
          setError(t("coordinatorDetails.loadError"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPage();
    return () => {
      cancelled = true;
    };
  }, [
    cocoId,
    coordinatorAccessDenied,
    coordinatorCacheKey,
    details,
    hydratedCacheKey,
    batchIndex,
    pageHistory,
    pageIndex,
    queryKey,
    reloadVersion,
    requestPage,
    t,
    tokens?.idToken,
  ]);

  useEffect(() => {
    if (visibleCaseTabs.length === 0 || visibleCaseTabs.includes(selectedCaseTab)) return;
    setSelectedCaseTab(visibleCaseTabs[0]);
    setPageIndex(0);
  }, [selectedCaseTab, visibleCaseTabs]);

  const totalPages = Math.max(1, Math.ceil((details?.totalSize || 0) / pageSize));

  const loadNextPage = async () => {
    if (loading || pageIndex + 1 >= totalPages) return;
    setPageIndex((index) => index + 1);
  };

  const loadPreviousPage = () => {
    if (pageIndex === 0 || loading) return;
    setPageIndex((index) => index - 1);
  };

  if (coordinatorAccessDenied) {
    return <Navigate to="/" replace />;
  }

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
    setPageIndex(0);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort aria-hidden="true" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp aria-hidden="true" />
    ) : (
      <FaSortDown aria-hidden="true" />
    );
  };

  const renderSortableHeader = (key, label) => (
    <th
      scope="col"
      aria-sort={
        sortConfig.key === key
          ? sortConfig.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button type="button" className="table-sort-button" onClick={() => handleSort(key)}>
        <span>{label}</span>
        {renderSortIcon(key)}
      </button>
    </th>
  );

  const renderCaseTab = (tabKey, label) => (
    <button
      key={tabKey}
      type="button"
      className={`case-tab-button ${activeCaseTab === tabKey ? "active" : ""}`}
      onClick={() => {
        setSelectedCaseTab(tabKey);
        if (
          tabKey !== CASE_TABS.ALL
          && tabKey !== CASE_TABS.BHP
        ) {
          setMasterScheduleFilter(MASTER_SCHEDULE_FILTERS.ALL);
        }
        setPageIndex(0);
      }}
      aria-pressed={activeCaseTab === tabKey}
    >
      <span>{label}</span>
      <span className="case-tab-count">{caseTabCounts[tabKey] || 0}</span>
    </button>
  );

  const formatPercentage = (value) => `${Number(value || 0).toFixed(2)}%`;
  const summaryCards = [
    { key: "totalClients", label: t("coordinatorDetails.summary.totalClients"), value: summary.totalClients || 0, icon: FaUsers },
    { key: "ardClients", label: t("coordinatorDetails.summary.ardClients"), value: summary.ardClients || 0, icon: FaUsers },
    { key: "bhpClients", label: t("coordinatorDetails.summary.bhpClients"), value: getEnglishClaimClients(summary), icon: FaUsers },
    { key: "retainersSigned", label: t("coordinatorDetails.summary.retainersSigned"), value: summary.retainersSigned || 0, icon: FaFileSignature },
    { key: "retainersPending", label: t("coordinatorDetails.summary.retainersPending"), value: summary.retainersPending || 0, icon: FaFileSignature },
    { key: "retainersSignedPercentage", label: t("coordinatorDetails.summary.retainersSignedPercentage"), value: formatPercentage(summary.retainersSignedPercentage), icon: FaFileSignature },
    { key: "questionnairesCompleted", label: t("coordinatorDetails.summary.questionnairesCompleted"), value: summary.questionnairesCompleted || 0, icon: FaClipboardCheck },
    { key: "questionnairesPending", label: t("coordinatorDetails.summary.questionnairesPending"), value: summary.questionnairesPending || 0, icon: FaClipboardCheck },
    { key: "questionnairesCompletedPercentage", label: t("coordinatorDetails.summary.questionnairesCompletedPercentage"), value: formatPercentage(summary.questionnairesCompletedPercentage), icon: FaClipboardCheck },
    { key: "discontinuanceTotal", label: t("coordinatorDetails.summary.discontinuanceTotal"), value: summary.discontinuanceTotal || 0, icon: FaClipboardCheck },
    { key: "discontinuanceSubmitted", label: t("coordinatorDetails.summary.disputesSubmitted"), value: summary.discontinuanceSubmitted || 0, icon: FaClipboardCheck },
    { key: "discontinuanceSubmittedPercentage", label: t("coordinatorDetails.summary.disputesSubmittedPercentage"), value: formatPercentage(summary.discontinuanceSubmittedPercentage), icon: FaClipboardCheck },
  ];

  return (
    <div className="view-container">
      <div className="portal-page-breadcrumb">
        <Breadcrumb
          items={[
            {
              label: t("breadcrumb.coordinatorDashboard"),
              link: "/",
              state: { activePortal: "coordinator" },
            },
            { label: t("breadcrumb.coordinatorDetails") },
          ]}
        />
      </div>

      <header className="clients-page-header coordinator-details-header">
        <div className="clients-title-group">
          <span className="clients-title-kicker">{t("portal.coordinator")}</span>
          <h2>{cleanCoCoName(details?.cocoName) || t("coordinatorDetails.unknownCoco")}</h2>
          <p className="coordinator-details-subtitle">{details?.cocoId || cocoId}</p>
        </div>
        <div className="clients-total-card" aria-live="polite">
          <span className="clients-total-icon"><FaUsers aria-hidden="true" /></span>
          <span className="clients-total-copy">
            <strong>{formatClientCount(details?.totalSize || 0)}</strong>
            <small>{t("coordinatorDetails.filteredClients")}</small>
          </span>
        </div>
      </header>

      <CollapsibleSummary title={t("clientsTable.summary.title.ALL")}>
        <section className="summary-card-grid" aria-label={t("coordinatorDetails.summaryLabel")}>
          {summaryCards.map(({ key, label, value, icon: Icon }) => (
            <article className="summary-card" key={key}>
              <span className="summary-card-icon"><Icon aria-hidden="true" /></span>
              <span className="summary-card-copy">
                <strong>{typeof value === "number" ? formatClientCount(value) : value}</strong>
                <small>{label}</small>
              </span>
            </article>
          ))}
        </section>
      </CollapsibleSummary>

      {visibleCaseTabs.length > 0 && (
        <div className="case-tabs-container" role="group" aria-label={t("clientsTable.case")}>
          {visibleCaseTabs.map((tabKey) =>
            renderCaseTab(
              tabKey,
              {
                [CASE_TABS.ALL]: t("clientsTable.allCases"),
                [CASE_TABS.BHP]: t("clientsTable.englishClaim"),
                [CASE_TABS.ARD]: t("clientsTable.dutchClaim"),
              }[tabKey],
            ),
          )}
        </div>
      )}

      <div className="filter-container coordinator-details-filter-container">
        <div className="filter-heading">
          <span className="filter-heading-icon"><FaFilter aria-hidden="true" /></span>
          <p className="note">{t("filters")}</p>
        </div>
        <div
          className={`coordinator-filters ${
            features.masterScheduleFilters
            && (
              activeCaseTab === CASE_TABS.ALL
              || activeCaseTab === CASE_TABS.BHP
            )
              ? "expanded-master-schedule"
              : ""
          }`}
        >
          <div className="filter-item-container search-filter">
            <label className="sr-only" htmlFor="coordinator-details-search">
              {t("coordinatorDetails.search")}
            </label>
            <FaSearch className="search-input-icon" aria-hidden="true" />
            <input
              id="coordinator-details-search"
              className="cpf-filter-input"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setPageIndex(0);
              }}
              placeholder={t("coordinatorDetails.search")}
            />
          </div>
          <select value={retainerFilter} onChange={(event) => {
            setRetainerFilter(event.target.value);
            setPageIndex(0);
          }} aria-label={t("coordinatorDashboard.retainerFilter")}>
            <option value="ALL">{t("coordinatorDashboard.retainerOptions.ALL")}</option>
            <option value="SIGNED">{t("coordinatorDashboard.retainerOptions.SIGNED")}</option>
            <option value="PENDING">{t("coordinatorDashboard.retainerOptions.PENDING")}</option>
          </select>
          <select value={questionnaireFilter} onChange={(event) => {
            setQuestionnaireFilter(event.target.value);
            setPageIndex(0);
          }} aria-label={t("coordinatorDashboard.questionnaireFilter")}>
            <option value="ALL">{t("coordinatorDashboard.questionnaireOptions.ALL")}</option>
            <option value="COMPLETED">{t("coordinatorDashboard.questionnaireOptions.COMPLETED")}</option>
            <option value="PENDING">{t("coordinatorDashboard.questionnaireOptions.PENDING")}</option>
          </select>
          {features.masterScheduleFilters
            && (
              activeCaseTab === CASE_TABS.ALL
              || activeCaseTab === CASE_TABS.BHP
            ) && (
              <select
                value={masterScheduleFilter}
                onChange={(event) => {
                  setMasterScheduleFilter(event.target.value);
                  setPageIndex(0);
                }}
                aria-label={t("coordinatorDashboard.masterScheduleFilter")}
              >
                <option value={MASTER_SCHEDULE_FILTERS.ALL}>
                  {t("clientsTable.masterScheduleOptions.ALL")}
                </option>
                {MASTER_SCHEDULE_FILTER_VALUES.map((value) => (
                  <option value={value} key={value}>
                    {t(`clientsTable.masterScheduleOptions.${value}`)}
                  </option>
                ))}
              </select>
            )}
        </div>
        <div className="page-selector">
          <label htmlFor="coordinator-details-page-size">{t("clientsTable.pageSize")}</label>
          <select id="coordinator-details-page-size" value={pageSize} onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPageIndex(0);
          }}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="dashboard-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setReloadVersion((version) => version + 1)}>{t("coordinatorDashboard.retry")}</button>
        </div>
      )}

      <div className="client-table-shell">
        <div className="client-table-scroll">
          <table className="client-table coordinator-details-table">
            <thead>
              <tr>
                {renderSortableHeader("partyName", t("coordinatorDetails.columns.clientName"))}
                {renderSortableHeader("cpf", t("clientsTable.cpf"))}
                {renderSortableHeader("caseType", t("clientsTable.case"))}
                {renderSortableHeader("matterStatus", t("clientsTable.matterStatus"))}
                {renderSortableHeader("retainerSignedDate", t("clientsTable.retainerSignedDate"))}
                {renderSortableHeader("questionnaireCompletedAt", t("clientsTable.questionnaireCompletedDate"))}
                {renderSortableHeader("bhpMasterSchedule", t("clientsTable.masterSchedule"))}
              </tr>
            </thead>
            <tbody>
              {!loading && !error && paginatedClients.length > 0 ? (
                paginatedClients.map((client) => (
                  <tr
                    key={`${client.partyId}-${client.matterId}`}
                    className="clickable-table-row"
                    tabIndex="0"
                    onClick={() => navigate(
                      `/coordinator/${encodeURIComponent(cocoId)}/clients/${encodeURIComponent(client.matterId)}`,
                    )}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(
                          `/coordinator/${encodeURIComponent(cocoId)}/clients/${encodeURIComponent(client.matterId)}`,
                        );
                      }
                    }}
                  >
                    <td data-label={t("coordinatorDetails.columns.clientName")} className="client-primary-link">
                      {client.partyName || "-"}
                    </td>
                    <td data-label={t("clientsTable.cpf")}>{client.cpf || "-"}</td>
                    <td data-label={t("clientsTable.case")}>
                      <span className={`case-badge case-badge-${getCaseProgram(client.caseType).toLowerCase()}`}>
                        {getCaseShortLabel(client.caseType, t)}
                      </span>
                    </td>
                    <td data-label={t("clientsTable.matterStatus")}>
                      {translateMatterStatus(client.matterStatus, t)}
                    </td>
                    <td data-label={t("clientsTable.retainerSignedDate")}>
                      {client.retainerSignedDate
                        ? formatDateToYYYYMMDD(client.retainerSignedDate)
                        : t("clientsTable.emptyStates.notYetSigned")}
                    </td>
                    <td data-label={t("clientsTable.questionnaireCompletedDate")}>
                      {client.questionnaireCompletedAt
                        ? formatDateToYYYYMMDD(client.questionnaireCompletedAt)
                        : t("clientsTable.emptyStates.notYetCompleted")}
                    </td>
                    <td data-label={t("clientsTable.masterSchedule")}>
                      {client.bhpMasterSchedule || t("clientsTable.emptyStates.noMasterSchedule")}
                    </td>
                  </tr>
                ))
              ) : !loading && !error ? (
                <tr>
                  <td colSpan="7" className="empty-table-cell">
                    {t("clientsTable.noClientsFound")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {loading && (
          <div className="loading-row">
            <div className="flex-center">
              <FaCog className="spinner-cog" />
            </div>
          </div>
        )}
      </div>

      {!loading && !error && paginatedClients.length > 0 && (
        <div className="pagination-container">
          <button type="button" className="pagination-button" onClick={loadPreviousPage} disabled={pageIndex === 0 || loading}>
            <FaChevronLeft aria-hidden="true" /><span>{t("clientsTable.previous")}</span>
          </button>
          <span className="pagination-info">{t("clientsTable.page")} <strong>{pageIndex + 1}</strong> {t("clientsTable.of")} <strong>{totalPages}</strong></span>
          <button type="button" className="pagination-button" onClick={loadNextPage} disabled={loading || pageIndex + 1 >= totalPages}>
            <span>{t("clientsTable.next")}</span><FaChevronRight aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CoordinatorCoCoDetails;
